import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Avatar,
  Tabs,
  Tab,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Grid,
  FormGroup,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { csAdminApi } from '../services/api';
const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    expiryAlerts: true,
    meetingReminders: true,
    systemUpdates: false,
  });

  useEffect(() => {
    csAdminApi
      .getProfile()
      .then((res) => {
        if (res.data?.data) updateUser(res.data.data);
      })
      .catch((err) => console.error('Profile load:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const handlePreferenceChange = (field) => (event) => {
    setPreferences((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSavePreferences = async () => {
    try {
      setLoading(true);
      await csAdminApi.updateUserPreferences(preferences);
      setError('');
      // TODO: Show success message
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Profile Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
        >
          {user?.name?.[0] || user?.username?.[0] || 'U'}
        </Avatar>
        <Typography variant="h4" gutterBottom>
          {user?.name || user?.username}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {user?.role?.replace('_', ' ')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {user?.email}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        To update your password, open the <strong>Security</strong> tab below or use{' '}
        <strong>Change Password</strong> from the account menu (top right).
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Personal Info" />
          <Tab label="Security" />
          <Tab label="Preferences" />
        </Tabs>
      </Box>

      {/* Personal Info Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Basic Information" />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1">
                      {user?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {user?.email || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Role
                    </Typography>
                    <Typography variant="body1">
                      {user?.role?.replace('_', ' ') || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Member Since
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(user?.createdDate)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/profile/change-password')}>
                  Change Password
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {user?.role === 'COMPANY_SECRETARY' && user?.assignedCompanies && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Assigned Companies" />
                <CardContent>
                  {user.assignedCompanies.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No companies assigned
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {user.assignedCompanies.map((company) => (
                        <Box key={company.id}>
                          <Typography variant="body1">{company.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Assigned: {formatDate(company.assignmentDate)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Security Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Password" />
              <CardContent>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Last changed: {formatDate(user?.lastPasswordChange)}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/profile/change-password')}
                >
                  Change Password
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Preferences Tab */}
      {activeTab === 2 && (
        <Card>
          <CardHeader title="Notification Preferences" />
          <CardContent>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications}
                    onChange={handlePreferenceChange('emailNotifications')}
                  />
                }
                label="Email Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.expiryAlerts}
                    onChange={handlePreferenceChange('expiryAlerts')}
                  />
                }
                label="Expiry Alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.meetingReminders}
                    onChange={handlePreferenceChange('meetingReminders')}
                  />
                }
                label="Meeting Reminders"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.systemUpdates}
                    onChange={handlePreferenceChange('systemUpdates')}
                  />
                }
                label="System Updates"
              />
            </FormGroup>
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              onClick={handleSavePreferences}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Preferences'}
            </Button>
          </CardActions>
        </Card>
      )}
    </Container>
  );
};

export default Profile;
